import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { SearchSelect } from '../../components/SearchSelect';
import { Field, FormGrid, NumberInput, SelectInput, TextArea, TextInput } from '../../components/ui';
import { money } from '../../lib/format';
import {
  computeLine, computeTotals, deriveUnitPrice, emptyLine, lineFromRecord, lineToPayload,
  toNum, type SalesLineForm,
} from '../../lib/sales';
import type { Contract, ContractLine, Customer, Item } from '../../lib/types';

function today(): string { return new Date().toISOString().slice(0, 10); }

export interface ContractEditorProps {
  // 传入已存在的草稿合同即为编辑模式；null/undefined 为新建草稿。
  initial?: Contract | null;
  onCancel: () => void;
  onSaved: (saved: Contract) => void;
}

// 销售合同新建 / 草稿编辑（独立于报价转合同）。新建走 POST /api/contracts?status=draft，
// 编辑走 PATCH /api/contracts/:id；后端强制：仅草稿可编辑、非管理员仅本人草稿。
export function ContractEditor({ initial, onCancel, onSaved }: ContractEditorProps) {
  const isEdit = !!initial?.id;
  const seq = useRef(1);
  const nextKey = () => `L${seq.current++}`;

  const [customerId, setCustomerId] = useState<string | null>(initial?.customer_id ?? null);
  const [customerLabel, setCustomerLabel] = useState<string>(initial?.customer_name ?? '');
  const [contractDate, setContractDate] = useState(initial?.contract_date?.slice(0, 10) || today());
  const [dueDate, setDueDate] = useState(initial?.delivery_due_date?.slice(0, 10) || '');
  const [poNo, setPoNo] = useState(initial?.po_no ?? '');
  const [termsEs, setTermsEs] = useState(initial?.terms_es ?? '');
  const [lines, setLines] = useState<(SalesLineForm & { id?: string })[]>(() => {
    const src = initial?.lines;
    if (src && src.length) return src.map((l: ContractLine) => ({...lineFromRecord(l, nextKey()),id:l.id,manual_price:!!l.discount_rule_snapshot?.overridden}));
    return [emptyLine(nextKey())];
  });

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<string | null>(null);

  const totals = useMemo(() => computeTotals(lines), [lines]);
  const [pricingBusy, setPricingBusy] = useState(false);
  const pricingKey = JSON.stringify([customerId, contractDate, lines.map(l=>[l.key,l.item_id,l.quantity,l.list_price,l.manual_price])]);
  useEffect(() => {
    if (!customerId) {setPricingBusy(false);return;}
    let cancelled = false;
    setPricingBusy(true);
    void Promise.all(lines.filter(l=>l.item_id).map(async l=>({key:l.key,data:(await api.post<{data:{rule_name:string;list_price:number;discount_percentage:number;unit_price:number}|null}>('/api/discount-rules/preview',{
      customer_id:customerId,item_id:l.item_id,date:contractDate,currency:'USD',quantity:toNum(l.quantity),list_price:toNum(l.list_price),
    })).data}))).then(results=>{
      if(cancelled)return;
      setLines(prev=>prev.map(l=>{const hit=results.find(r=>r.key===l.key);if(!hit)return l;
        if(hit.data&&l.manual_price)return {...l,discount_rule_name:hit.data.rule_name};
        if(hit.data)return {...l,list_price:String(hit.data.list_price),discount_rule_name:hit.data.rule_name,discount_percentage:String(hit.data.discount_percentage),discount_amount:'0',unit_price:String(hit.data.unit_price)};
        if(l.manual_price)return {...l,discount_rule_name:undefined};
        if(l.discount_rule_name)return {...l,discount_rule_name:undefined,discount_percentage:'0',discount_amount:'0',unit_price:l.list_price};
        return l;
      }));setErr(null);
    }).catch(e=>{if(!cancelled)setErr(e instanceof ApiError?e.message:'自动折扣计算失败');}).finally(()=>{if(!cancelled)setPricingBusy(false);});
    return ()=>{cancelled=true;};
  },[pricingKey]);

  const patchLine = (key: string, patch: Partial<SalesLineForm>, recomputePrice = true) => {
    setLines((prev) => prev.map((l) => {
      if (l.key !== key) return l;
      const merged = { ...l, ...patch };
      if (recomputePrice && !merged.manual_price) {
        merged.unit_price = String(deriveUnitPrice(merged));
      }
      return merged;
    }));
  };
  const editUnitPrice = (key: string, value: string) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, unit_price: value, manual_price: true } : l)));
  };
  const addLine = () => setLines((prev) => [...prev, emptyLine(nextKey())]);
  const removeLine = (key: string) => setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));

  const applyItem = (key: string, item: Item | null) => {
    if (!item) {
      setLines((prev) => prev.map((l) => (l.key === key ? { ...l, item_id: null } : l)));
      return;
    }
    setLines((prev) => prev.map((l) => {
      if (l.key !== key) return l;
      const price = String(item.sale_price ?? 0);
      const desc = !l.description_es.trim() ? (item.name_es || item.name_zh || '') : l.description_es;
      return {
        ...l, item_id: item.id, description_es: desc, uom: item.stock_uom || l.uom,
        list_price: price, discount_percentage: l.discount_percentage, discount_amount: l.discount_amount,
        unit_price: l.manual_price ? l.unit_price : String(deriveUnitPrice({
          list_price: price, discount_percentage: l.discount_percentage, discount_amount: l.discount_amount, quantity: l.quantity,
        })),
      };
    }));
  };

  const validate = (): boolean => {
    if(pricingBusy){setFieldErr('自动折扣计算中，请稍后保存');return false;}
    if(lines.some(l=>l.manual_price&&l.discount_rule_name&&!l.price_override_reason?.trim())){setFieldErr('覆盖规则价格时必须填写原因');return false;}
    if (!customerId) { setFieldErr('请选择客户'); return false; }
    if (!contractDate) { setFieldErr('合同日期为必填'); return false; }
    if (!lines.length) { setFieldErr('至少需要一个明细行'); return false; }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.description_es.trim()) { setFieldErr(`第 ${i + 1} 行：西语描述不能为空`); return false; }
      if (toNum(l.quantity) <= 0) { setFieldErr(`第 ${i + 1} 行：数量需大于 0`); return false; }
      if (toNum(l.unit_price) < 0) { setFieldErr(`第 ${i + 1} 行：单价不能为负`); return false; }
    }
    setFieldErr(null);
    return true;
  };

  const save = async () => {
    setErr(null);
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        customer_id: customerId,
        contract_date: contractDate,
        delivery_due_date: dueDate || null,
        po_no: poNo || null,
        terms_es: termsEs || null,
        lines: lines.map(l=>({...lineToPayload(l),id:l.id,manual_price:l.manual_price,price_override_reason:l.price_override_reason})),
      };
      const saved = isEdit
        ? await api.patch<{ data: Contract }>(`/api/contracts/${initial!.id}`, payload).then((r) => r.data)
        : await api.post<{ data: Contract }>('/api/contracts', { ...payload, status: 'draft' }).then((r) => r.data);
      onSaved(saved);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head">
        <div style={{ minWidth: 0 }}>
          <h2>{isEdit ? `编辑合同草稿 · ${initial!.number}` : '新建销售合同草稿'}</h2>
          <div className="desc">
            {isEdit ? '仅草稿可编辑 · PATCH /api/contracts/:id' : 'POST /api/contracts · 新建合同固定 USD 计价，保存后为草稿'}
          </div>
        </div>
        <div className="spacer" />
        <span className="muted" style={{ fontSize: 12 }}>常规草稿保存不弹窗</span>
      </div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}

        <FormGrid>
          <Field label="客户" required span>
            <SearchSelect<Customer>
              endpoint="/api/customers"
              value={customerId}
              label={customerLabel}
              ariaLabel="选择客户"
              placeholder="输入客户名称 / 编码 / 税号检索…"
              optionTitle={(c) => c.legal_name}
              optionMeta={(c) => c.code}
              onChange={(c) => { setCustomerId(c?.id ?? null); setCustomerLabel(c ? c.legal_name : ''); }}
            />
          </Field>
          <Field label="合同日期" required>
            <TextInput type="date" value={contractDate} onChange={(e) => setContractDate(e.target.value)} />
          </Field>
          <Field label="交付到期日" hint="可留空">
            <TextInput type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
          <Field label="币种" hint="新合同固定 USD">
            <TextInput value="USD" disabled readOnly />
          </Field>
          <Field label="客户 PO 号" hint="采购订单号，交货单 / REMITO 会沿用；留空则不打印。">
            <TextInput value={poNo} placeholder="Orden de compra (PO)" onChange={(e) => setPoNo(e.target.value)} />
          </Field>
          <Field label="西语条款（打印展示）" span>
            <TextArea value={termsEs} placeholder="Términos y condiciones…" onChange={(e) => setTermsEs(e.target.value)} />
          </Field>
        </FormGrid>

        {fieldErr ? <div className="notice warn" style={{ margin: '12px 0 0' }}>{fieldErr}</div> : null}

        <div style={{ marginTop: 16 }}>
          <div className="detail-head" style={{ marginBottom: 8 }}>
            <h3 style={{ fontSize: 14 }}>明细行</h3>
            <div className="spacer" />
            <button className="btn" onClick={addLine} type="button">+ 添加明细</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="line-grid">
              <thead>
                <tr>
                  <th style={{ width: 210 }}>物料（可选）</th>
                  <th style={{ minWidth: 220 }}>西语描述</th>
                  <th style={{ width: 88 }} className="col-r">数量</th>
                  <th style={{ width: 96 }}>单位</th>
                  <th style={{ width: 108 }} className="col-r">原价</th>
                  <th style={{ width: 84 }} className="col-r">折扣%</th>
                  <th style={{ width: 96 }} className="col-r">折扣额</th>
                  <th style={{ width: 108 }} className="col-r">净单价</th>
                  <th style={{ width: 76 }} className="col-r">IVA%</th>
                  <th style={{ width: 118 }} className="col-r">行金额</th>
                  <th style={{ width: 34 }} aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const c = computeLine(l);
                  return (
                    <tr key={l.key}>
                      <td>
                        <SearchSelect<Item>
                          endpoint="/api/items"
                          value={l.item_id ?? null}
                          label=""
                          limit={15}
                          ariaLabel="选择物料"
                          placeholder="编码 / 中英文名…"
                          optionTitle={(i) => i.name_es || i.name_zh}
                          optionMeta={(i) => i.code}
                          onChange={(it) => applyItem(l.key, it)}
                        />
                      </td>
                      <td><TextInput value={l.description_es} placeholder="Descripción (ES)" onChange={(e) => patchLine(l.key, { description_es: e.target.value })} /></td>
                      <td><NumberInput value={l.quantity} onChange={(e) => patchLine(l.key, { quantity: e.target.value })} /></td>
                      <td><TextInput value={l.uom} onChange={(e) => patchLine(l.key, { uom: e.target.value }, false)} /></td>
                      <td><NumberInput value={l.list_price} onChange={(e) => patchLine(l.key, { list_price: e.target.value })} /></td>
                      <td><NumberInput value={l.discount_percentage} onChange={(e) => patchLine(l.key, { discount_percentage: e.target.value })} /></td>
                      <td><NumberInput value={l.discount_amount} onChange={(e) => patchLine(l.key, { discount_amount: e.target.value })} /></td>
                      <td><NumberInput value={l.unit_price} onChange={(e) => editUnitPrice(l.key, e.target.value)} />
                        {l.discount_rule_name&&<small>规则：{l.discount_rule_name}</small>}
                        {l.manual_price&&<TextInput placeholder="人工改价原因" value={l.price_override_reason||''} onChange={e=>patchLine(l.key,{price_override_reason:e.target.value},false)}/>}
                        {l.manual_price&&l.discount_rule_name&&<button className="btn" type="button" onClick={()=>patchLine(l.key,{manual_price:false,price_override_reason:''},false)}>恢复规则价</button>}
                      </td>
                      <td><SelectInput value={l.tax_rate} onChange={(e) => patchLine(l.key, { tax_rate: e.target.value }, false)}>
                        <option value="0">0</option>
                        <option value="21">21</option>
                        <option value="10.5">10.5</option>
                        <option value="27">27</option>
                      </SelectInput></td>
                      <td className="row-total">{money(c.lineTotal, 'USD')}</td>
                      <td><button type="button" className="row-del" title="删除该行" onClick={() => removeLine(l.key)}>×</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="editor-totals">
            <div className="t"><span className="k">净额 Subtotal</span><span className="v">{money(totals.subtotal, 'USD')}</span></div>
            <div className="t"><span className="k">IVA</span><span className="v">{money(totals.tax_total, 'USD')}</span></div>
            <div className="t grand"><span className="k">含税总额 Total</span><span className="v">{money(totals.grand_total, 'USD')}</span></div>
          </div>
        </div>

        <div className="detail-actions" style={{ marginTop: 16 }}>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? '保存中…' : isEdit ? '保存草稿' : '创建草稿'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
          <span className="muted" style={{ fontSize: 11.5, marginLeft: 6 }}>保存后仍为草稿，可在列表行内确认后进入履约流程。</span>
        </div>
      </div>
    </section>
  );
}
